import { useState, useRef, useEffect } from "react";

interface Center {
  id: number;
  business_name: string;
}

interface InputTagsProps {
  field?: string;
  onTagsChange: (centreTagId: number | number[]) => void;
  allCenters: Center[];
  selectedValue: number[];
  allcentreAccess?: number;
}

export default function InputTags(props: InputTagsProps) {
  const { field, onTagsChange, allCenters, selectedValue, allcentreAccess } =
    props;
  const [tags, setTags] = useState<number[]>([]);
  const [input, setInput] = useState<string>("");
  const [filteredTags, setFilteredTags] = useState<Center[]>(allCenters);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addTag = (tag: number) => {
    if (!tags.includes(tag)) {
      const updatedTags = [...tags, tag];
      setTags(updatedTags); // want to set id not name
      // const centreTagId = getTagId(tag);
      // onTagsChange(updatedIds.length === 1 ? updatedIds[0] : updatedIds); // <-- Notify parent
      if (onTagsChange) onTagsChange(updatedTags); // Notify parent
    }
    setInput("");
    setDropdownOpen(true);
  };

  const removeTag = (tagToRemove: number) => {
    const updatedTags = tags.filter((tag) => tag !== tagToRemove);
    setTags(updatedTags);
    // const centreTagId = getTagId(tag);
    // onTagsChange(updatedTags.length === 1 ? updatedTags[0] : updatedTags); // <-- Notify parent
    if (onTagsChange) onTagsChange(updatedTags); // Notify parent
    setDropdownOpen(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInput(value);
    setDropdownOpen(true);

    if (value) {
      setFilteredTags(
        allCenters.filter((tag) =>
          tag?.business_name.toLowerCase().includes(value.toLowerCase())
        )
      );
    } else {
      setFilteredTags(allCenters);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && filteredTags.length > 0) {
      addTag(filteredTags[0].id);
      e.preventDefault();
    } else if (e.key === "Backspace" && !input && tags.length > 0) {
      removeTag(tags[tags.length - 1]);
    }
  };

  const toggleTag = (tag: number) => {
    if (tags.includes(tag)) {
      removeTag(tag);
    } else {
      addTag(tag);
    }
  };

  const getTagId = (tag: string): string | null => {
    return tag
      ? String(allCenters.find((center) => center.business_name === tag)?.id) ||
          null
      : null;
  };

  const focusInput = () => {
    inputRef.current?.focus();
    setDropdownOpen(true);
  };

  // const getBusinessName = (tag: string): string | null => {
  //   return tag
  //     ? allCenters.find((center) => center.id === tag)?.business_name || null
  //     : null;
  // };

  const convertIdsToBusinessNames = (
    ids: number[],
    centers: Center[]
  ): string[] => {
    return ids
      .map((id) => centers.find((center) => center.id === id)?.business_name)
      .filter(Boolean) as string[];
  };

  useEffect(() => {
    setTags(selectedValue || []);
  }, [selectedValue, allCenters]);

  const getBusinessNameById = (id: number) => {
    return allCenters.find((center) => center.id === id)?.business_name || "";
  };

  return (
    <div
      className={`position-relative border radius-8px p-10px ${
        dropdownOpen ? "border-grey-light" : "border-off-white"
      } ${allcentreAccess === 1 ? "disable-div" : ""}`}
      ref={containerRef}
      onClick={focusInput}
      style={{ cursor: "text" }}
    >
      <div className="d-flex flex-wrap align-items-center gap-8 text-break">
        {tags.map((tag) => (
          !allcentreAccess && getBusinessNameById(tag) !== "" ? 
          <>
            <span
              key={tag}
              className="bg-teal-light py-3px ps-13px pe-9px radius-10px d-flex gap-4 align-items-center text-break"
            >
              <div className="f-16 fw-400 text-black">
                {getBusinessNameById(tag)}
              </div>

              <svg
                className="min-w-24px cursor-pointer"
                onClick={() => removeTag(tag)}
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.68867 6.81133C7.31703 6.18297 8.33579 6.18297 8.96414 6.81133L12.3773 10.2245L15.7906 6.81133C16.4189 6.18297 17.4377 6.18297 18.066 6.81133C18.6944 7.43968 18.6944 8.45844 18.066 9.0868L14.6528 12.5L18.066 15.9132C18.6944 16.5416 18.6944 17.5603 18.066 18.1887C17.4377 18.817 16.4189 18.817 15.7906 18.1887L12.3773 14.7755L8.96414 18.1887C8.33579 18.817 7.31703 18.817 6.68867 18.1887C6.06032 17.5603 6.06032 16.5416 6.68867 15.9132L10.1019 12.5L6.68867 9.0868C6.06032 8.45844 6.06032 7.43968 6.68867 6.81133Z"
                  fill="black"
                  stroke="#C0F2EE"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </> : ""
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="border-0 flex-grow-1"
          placeholder="Type to search..."
          style={{ outline: "none", minWidth: "100px" }}
          disabled={allcentreAccess === 1}
        />
        <svg
          className={`transition ${dropdownOpen ? "rotate-180" : ""}`}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10.5 4.0575L9.4425 3L6 6.435L2.5575 3L1.5 4.0575L6 8.5575L10.5 4.0575Z"
            fill="#757575"
          />
        </svg>
      </div>

      {dropdownOpen && filteredTags.length > 0 && (
        <div className="position-absolute z-1 inputtag-dropdown overflow-auto disabled">
          {filteredTags.map((tag) => (
            <div
              key={tag?.id}
              className="px-15px py-8px d-flex align-items-center hover-bg-off-white text-break"
              style={{ cursor: "pointer" }}
              // onClick={(e) => {
              //   e.stopPropagation(); // Prevent event bubbling
              //   toggleTag(tag?.business_name);
              // }}
            >
              <div className="theme-check">
                <input
                  type="checkbox"
                  className="theme-check-input"
                  checked={tags.includes(tag?.id)}
                  id={tag.id.toString()}
                  readOnly
                  onClick={() => toggleTag(tag?.id)}
                />
                <label
                  htmlFor={tag.id.toString()}
                  className="theme-check-label lh-24px"
                >
                  {tag?.business_name}
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
